import React, { useMemo, useState } from 'react';
import { Alert, Badge, Card, Spinner } from 'flowbite-react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { HiCheck, HiOutlineExclamation } from 'react-icons/hi';
import classNames from 'classnames';

import SubTitle from '../../components/SubTitle';
import Button from '../../components/Button';
import { fetchMldMemberDeals } from '../../api/mld/actions';
import { mldQueryKeys } from '../../api/mld/queryKeys';
import type { MldDealView, MldLedgerEntryDto } from '../../api/mld/types';
import {
  MldDisplayLedgerEntry,
  MldFormatOptions,
  MldPlanNameLookup,
  formatCents,
  formatMldDate,
  formatMldTime,
  formatMldTimestamp,
  MldStepper,
  mldCancellationScheduledLine,
  mldDisplayLedger,
  mldFamilyLabel,
  mldHistoryWhat,
  mldImportFootnote,
  mldLastPaymentSummary,
  mldLedgerKindLabel,
  mldLifecycleStepper,
  mldMemberCardDates,
  mldNewestFirst,
  mldPaymentAmount,
  mldPaymentItem,
  mldPaymentSaleTypeLabel,
  mldPlanNameLookup,
  mldSplitImported,
  mldSubscriptionTypeLabel,
  mldWho,
} from '../mld/helpers';
import type { MldBack } from '../mld/mldListQuery';
import {
  MldPaymentDueHelp,
  MldPreviewLabel,
  MldStatusChip,
} from '../mld/MldStatusChip';
import { mldStrings } from '../mld/strings';
import { formatMessage, memberMldStrings as S } from './MemberMldTab.strings';

interface MemberMldTabProps {
  /** The DS customer `_id` of the member page. */
  customerId: string;
  /**
   * `false` until the operator opens the tab: flowbite mounts every tab
   * panel with the page, so the request waits for this instead.
   */
  active?: boolean;
  /** Time zone and "today" for the dates; tests inject them. */
  formatOptions?: MldFormatOptions;
}

type LedgerFilter = 'all' | 'sales' | 'failed' | 'statusChanges';

const LEDGER_FILTERS: readonly LedgerFilter[] = [
  'all',
  'sales',
  'failed',
  'statusChanges',
];

/**
 * TechSpec route 4: the chips are applied in ABO, on the merged ledger. The
 * rows never hold an `IMPORTED` entry (`mergeLedgers` takes it out, ADR-007
 * decision 7), so "Status changes" never counts the import.
 */
const matchesFilter = (
  entry: MldLedgerEntryDto,
  filter: LedgerFilter,
): boolean => {
  switch (filter) {
    case 'sales':
      return entry.kind === 'PAYMENT';
    case 'failed':
      return entry.kind === 'PAYMENT_FAILED';
    case 'statusChanges':
      return entry.kind !== 'PAYMENT' && entry.kind !== 'PAYMENT_FAILED';
    default:
      return true;
  }
};

interface MemberLedgerRow {
  key: string;
  /** A stored entry, or the display-only row of an imported payment. */
  entry: MldDisplayLedgerEntry;
  deal: MldDealView;
}

/**
 * Every deal's ledger in one list, newest first (2026-10-08 user decision: the
 * most recent entry on top, the same order as the deal detail). Each deal's
 * ledger is put in chronological order first and the merged list is then
 * reversed by `mldNewestFirst`, so on equal timestamps a deal's later entry
 * comes first, and between deals the later DS card comes first. Each deal's
 * ledger is the display ledger (ADR-005): an imported deal also shows its
 * snapshot payment, the same last payment the list and the deal detail show.
 *
 * The `IMPORTED` notes are not rows (ADR-007 decision 7): the import is where
 * the history starts, not something that happened to the member. They are
 * taken out before the sort, so they are in no row, filter or count, and the
 * earliest one is returned for the footnote under the ledger.
 */
const mergeLedgers = (
  deals: readonly MldDealView[],
): { rows: MemberLedgerRow[]; imported: MldDisplayLedgerEntry | null } => {
  const splits = deals.map((deal) => ({
    deal,
    ...mldSplitImported(mldDisplayLedger(deal)),
  }));
  const dated = splits.flatMap(({ deal, entries }) =>
    entries.map((entry, entryIndex) => ({
      at: entry.at,
      row: {
        key: `${deal.subscriptionId}:${entryIndex}:${entry.eventId}`,
        entry,
        deal,
      },
    })),
  );
  // The earliest import of all the member's deals: the same rule
  // `mldSplitImported` applies within one deal.
  const imported = mldSplitImported(
    splits.flatMap((split) => (split.imported ? [split.imported] : [])),
  ).imported;
  return { rows: mldNewestFirst(dated).map(({ row }) => row), imported };
};

const planName = (deal: MldDealView): string =>
  deal.plan?.name || deal.plan?.itemPriceId || mldStrings.unknown;

/** "AFY · Gold Monthly": the deal's family and its current plan. */
const dealName = (deal: MldDealView): string =>
  formatMessage(S.ledger.deal, {
    family: mldFamilyLabel(deal.family),
    item: planName(deal),
  });

const isPaymentEntry = (entry: MldLedgerEntryDto): boolean =>
  entry.kind === 'PAYMENT' || entry.kind === 'PAYMENT_FAILED';

/**
 * The Deal cell of a ledger row (ADR-007 decision 3): a payment names what it
 * paid for, its own plan ("AFY · Authorify Premium" on a payment made before
 * an upgrade) or what a one-time charge bought ("AFY · 52 printed book
 * credits (upsell offer)"); an imported payment names what its snapshot
 * invoice bought. Any other row, and a payment that carries neither (an old
 * record, a snapshot with no item), names the deal's plan.
 */
const rowDealName = (
  entry: MldLedgerEntryDto,
  deal: MldDealView,
  lookup: MldPlanNameLookup,
): string =>
  formatMessage(S.ledger.deal, {
    family: mldFamilyLabel(deal.family),
    item:
      (isPaymentEntry(entry) ? mldPaymentItem(entry, lookup) : null) ??
      planName(deal),
  });

const joinParts = (parts: Array<string | null | undefined>): string =>
  parts
    .filter((part): part is string => Boolean(part))
    .join(S.ledger.separator);

/**
 * The "What happened" detail after a payment's kind label, from codes only.
 * A sale-typed payment whose sale amount differs from what was paid reads
 * "$216.00 paid · $538.91 sale" (ADR-007 decision 11).
 */
const paymentDetail = (entry: MldLedgerEntryDto): string =>
  entry.kind === 'PAYMENT'
    ? joinParts([
        mldPaymentAmount(entry),
        mldPaymentSaleTypeLabel(entry.saleType, entry.trialPayment),
      ])
    : joinParts([
        formatCents(entry.amountCents, entry.currency),
        entry.failureReason || entry.invoiceId,
      ]);

/**
 * The "What happened" cell. Payments read "Payment · $49.00 · Billed-through";
 * an imported payment adds the deal detail's "Imported" badge after it. Every
 * other kind uses the deal detail's status-history wording ("Status set to
 * Active", "Plan changed from Authorify Premium to Authorify Platinum", "Set
 * to cancel on Nov 8"), so both screens read the same.
 */
const EntryWhat: React.FC<{
  entry: MldDisplayLedgerEntry;
  lookup: MldPlanNameLookup;
  options?: MldFormatOptions;
}> = ({ entry, lookup, options }) => {
  if (!isPaymentEntry(entry)) {
    return (
      <span className="font-medium text-neutral-900">
        {mldHistoryWhat(entry, lookup, options)}
      </span>
    );
  }
  const detail = paymentDetail(entry);
  return (
    <>
      <span className="font-medium text-neutral-900">
        {mldLedgerKindLabel(entry.kind)}
      </span>
      {detail && (
        <span className="text-neutral-700">
          {S.ledger.separator}
          {detail}
        </span>
      )}
      {entry.importedPayment && (
        <Badge
          color="gray"
          size="sm"
          className="ml-2 inline-flex w-fit align-middle"
          data-testid="mld-ledger-imported"
        >
          {mldStrings.detail.payments.imported}
        </Badge>
      )}
    </>
  );
};

/**
 * Who did it, in the one vocabulary for payments and status rows alike
 * (ADR-007 decision 1). An entry with no nameable who (the system actor of an
 * import, or an unknown origin) reads "—", as on the deal detail and the
 * list, never "Chargebee" (ADR-005).
 */
const entryWho = (entry: MldLedgerEntryDto): string =>
  mldWho(entry.actor, entry.operatorEmail) ?? mldStrings.none;

/**
 * The deal detail's Back target for "Open deal" (ADR-004): back to this
 * member, whose page then opens on its MLD tab.
 */
const memberBack = (customerId: string): MldBack => ({
  kind: 'member',
  to: `/customer-information/${customerId}`,
});

const Fact: React.FC<{ label: string; value: string; testId?: string }> = ({
  label,
  value,
  testId,
}) => (
  <div data-testid={testId}>
    <dt className="text-sm text-neutral-500">{label}</dt>
    <dd className="font-medium text-neutral-900">{value}</dd>
  </div>
);

/**
 * Trial → Active → On hold / Payment due → Cancelled, drawn from the deal's
 * history (ADR-007 decision 6), never from a stage's position:
 * - the current stage: dark top border on a tinted box;
 * - a visited stage: mid-grey top border and a check mark;
 * - a stage never reached: a dashed light border and its placeholder ("No
 *   trial", "Not scheduled", "Scheduled Nov 8"). `neutral-*` tokens only:
 *   ABO's `gray-50` is white and would not show.
 * Each stage also carries its state as a visually hidden word, so a screen
 * reader hears "Visited" or "Not reached", not just a colour.
 * A deal in Payment due gets the "(?)" help beside its current stage.
 */
const LifecycleStepper: React.FC<{
  stepper: MldStepper;
  paymentDue: boolean;
}> = ({ stepper, paymentDue }) => {
  return (
    <ol
      aria-label={S.card.stepperLabel}
      className="grid grid-cols-2 gap-2 md:grid-cols-4"
      data-testid="mld-deal-stepper"
    >
      {stepper.stages.map((stage) => (
        <li
          key={stage.key}
          aria-current={stage.current ? 'step' : undefined}
          data-testid={`mld-deal-stage-${stage.key}`}
          data-state={stage.state}
          className={classNames(
            'rounded-lg border-t-4 px-3 py-2',
            stage.state === 'current' && 'border-neutral-900 bg-neutral-100',
            stage.state === 'visited' && 'border-neutral-500 bg-white',
            stage.state === 'notReached' &&
              'border border-dashed border-neutral-300 bg-white',
          )}
        >
          <div className="flex items-center gap-1">
            <p
              className={classNames(
                'flex items-center gap-1 text-sm',
                stage.current
                  ? 'font-semibold text-neutral-900'
                  : 'text-neutral-700',
              )}
            >
              {stage.state === 'visited' && (
                <HiCheck
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-neutral-600"
                />
              )}
              {stage.label}
              <span className="sr-only" data-testid="mld-deal-stage-state">
                {formatMessage(S.card.stageState, { state: stage.stateLabel })}
              </span>
            </p>
            {paymentDue && stage.current && <MldPaymentDueHelp />}
          </div>
          {stage.ranges.map((range) => (
            <p key={range} className="text-xs text-neutral-600">
              {range}
            </p>
          ))}
          {stage.placeholder && (
            <p
              className="text-xs text-neutral-600"
              data-testid="mld-deal-stage-placeholder"
            >
              {stage.placeholder}
            </p>
          )}
        </li>
      ))}
    </ol>
  );
};

const DealCard: React.FC<{
  deal: MldDealView;
  /** The member page the card is on: "Open deal" comes back to it. */
  customerId: string;
  options?: MldFormatOptions;
}> = ({ deal, customerId, options }) => {
  const stepper = mldLifecycleStepper(deal, options);
  // No value twice (UI checklist): family and plan are in the card title,
  // "Cancels <date>" is in the header chip and "Active since <date>" /
  // "On hold since <date>" is in the stepper caption.
  const dates = mldMemberCardDates(deal, stepper, options);
  const scheduled = deal.cancelsOn
    ? mldCancellationScheduledLine(deal, options)
    : null;
  const headingId = `mld-deal-${deal.subscriptionId}`;
  return (
    <Card data-testid="mld-deal-card">
      <article aria-labelledby={headingId} className="flex flex-col gap-4">
        <header
          className="flex flex-wrap items-start justify-between gap-3"
          data-testid="mld-deal-card-header"
        >
          <div className="flex flex-col gap-1">
            <h3
              id={headingId}
              className="text-lg font-semibold text-neutral-900"
            >
              {dealName(deal)}
            </h3>
            <MldStatusChip deal={deal} options={options} />
            {scheduled && (
              <p
                className="text-sm text-neutral-600"
                data-testid="mld-deal-cancellation-scheduled"
              >
                {scheduled}
              </p>
            )}
          </div>
          <Link
            to={`/mld/${encodeURIComponent(deal.subscriptionId)}`}
            state={{ mldBack: memberBack(customerId) }}
            className="inline-flex min-h-[36px] items-center rounded-lg border border-neutral-300 px-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-100"
            data-testid="mld-deal-open"
          >
            {S.card.openDeal}
          </Link>
        </header>

        <LifecycleStepper
          stepper={stepper}
          paymentDue={deal.status === 'PAYMENT_DUE'}
        />

        <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Fact
            label={S.card.price}
            value={formatCents(deal.plan?.amountCents, deal.plan?.currency)}
          />
          <Fact
            label={S.card.subscriptionType}
            value={mldSubscriptionTypeLabel(deal.plan?.subscriptionType)}
          />
          {/* Before the dates: the last payment, then what comes next. */}
          <Fact
            label={S.card.lastPayment}
            value={mldLastPaymentSummary(deal.lastPayment, options)}
            testId="mld-deal-last-payment"
          />
          {dates.map((field) => (
            <Fact
              key={field.key}
              label={field.label}
              value={field.value}
              testId={`mld-deal-date-${field.key}`}
            />
          ))}
        </dl>
      </article>
    </Card>
  );
};

const LedgerFilterChips: React.FC<{
  value: LedgerFilter;
  onChange: (value: LedgerFilter) => void;
}> = ({ value, onChange }) => (
  <div
    role="group"
    aria-label={S.ledger.filterLabel}
    className="flex flex-wrap gap-2"
    data-testid="mld-ledger-filters"
  >
    {LEDGER_FILTERS.map((filter) => {
      const active = filter === value;
      return (
        <button
          key={filter}
          type="button"
          aria-pressed={active}
          onClick={() => onChange(filter)}
          className={classNames(
            'min-h-[36px] rounded-full border px-3 text-sm font-semibold transition-colors',
            active
              ? 'border-neutral-900 bg-neutral-900 text-white'
              : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100',
          )}
        >
          {S.ledger.filters[filter]}
        </button>
      );
    })}
  </div>
);

const MemberLedger: React.FC<{
  deals: readonly MldDealView[];
  options?: MldFormatOptions;
}> = ({ deals, options }) => {
  const [filter, setFilter] = useState<LedgerFilter>('all');
  const { rows, imported } = useMemo(() => mergeLedgers(deals), [deals]);
  // Every plan name the member's deals carry, for plan changes and payments.
  const lookup = useMemo(() => mldPlanNameLookup(deals), [deals]);
  const visible = rows.filter((row) => matchesFilter(row.entry, filter));
  const footnote = mldImportFootnote(imported, options);

  return (
    <Card data-testid="mld-ledger">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SubTitle>{S.ledger.heading}</SubTitle>
        <LedgerFilterChips value={filter} onChange={setFilter} />
      </div>
      {visible.length === 0 ? (
        <p className="text-sm text-neutral-600" data-testid="mld-ledger-empty">
          {S.ledger.empty}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm text-neutral-700">
            <thead className="text-xs uppercase tracking-wide text-neutral-600">
              <tr className="border-b border-neutral-200">
                <th scope="col" className="px-3 py-2 font-semibold">
                  {S.ledger.columns.when}
                </th>
                <th scope="col" className="px-3 py-2 font-semibold">
                  {S.ledger.columns.deal}
                </th>
                <th scope="col" className="px-3 py-2 font-semibold">
                  {S.ledger.columns.what}
                </th>
                <th scope="col" className="px-3 py-2 font-semibold">
                  {S.ledger.columns.who}
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map(({ key, entry, deal }) => (
                <tr
                  key={key}
                  className="border-b border-neutral-200 align-top"
                  data-testid="mld-ledger-row"
                  data-kind={entry.kind}
                >
                  <td className="whitespace-nowrap px-3 py-3 text-neutral-800">
                    {/* The date, then the time; the full instant on hover. The
                        space keeps "Oct 8 10:58 PM" one phrase when read aloud
                        or copied; a flex column ignores it in the layout. */}
                    <time
                      dateTime={entry.at}
                      title={formatMldTimestamp(entry.at, options)}
                      className="flex flex-col"
                    >
                      <span>{formatMldDate(entry.at, options)}</span>{' '}
                      <span
                        className="text-xs text-neutral-600"
                        data-testid="mld-ledger-time"
                      >
                        {formatMldTime(entry.at, options)}
                      </span>
                    </time>
                  </td>
                  <td className="px-3 py-3 text-neutral-800">
                    {rowDealName(entry, deal, lookup)}
                  </td>
                  <td className="px-3 py-3">
                    <EntryWhat
                      entry={entry}
                      lookup={lookup}
                      options={options}
                    />
                  </td>
                  <td className="break-all px-3 py-3 text-neutral-800">
                    {entryWho(entry)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Where the history starts: under the rows, in no filter or count. */}
      {footnote && (
        <p
          className="text-xs text-neutral-600"
          data-testid="mld-ledger-import-footnote"
        >
          {footnote}
        </p>
      )}
    </Card>
  );
};

/**
 * Member › MLD tab of Customer Information (native Member List Deal, mock
 * Option C): one card per Chargebee subscription in the order DS returns
 * (family and plan in the title, status chip in the header, lifecycle
 * stepper drawn from the history, price, subscription type, last payment and
 * the dates that matter now, a link to the deal), then one ledger of every
 * deal's payments and status changes, newest first like the deal detail, with
 * the date and time of each row, the plan or item it was for, who did it, filter
 * chips and, for an imported deal, the import footnote. Loads
 * `GET /v1/abo/mld/member/:customerId`. Read-only, and it repeats nothing
 * from the Subscriptions tab: no raw Chargebee status, period or links.
 */
const MemberMldTab: React.FC<MemberMldTabProps> = ({
  customerId,
  active = true,
  formatOptions,
}) => {
  const query = useQuery(
    mldQueryKeys.member(customerId),
    () => fetchMldMemberDeals(customerId),
    {
      enabled: Boolean(customerId) && active,
      refetchOnWindowFocus: false,
      // A failure shows the load-error notice at once; the operator retries.
      retry: false,
    },
  );

  // Read defensively: a payload without `deals` renders as "no deal".
  const deals = Array.isArray(query.data?.deals) ? query.data.deals : [];

  return (
    <div className="flex flex-col gap-4" data-testid="member-mld-tab">
      <div className="flex flex-wrap items-center gap-3">
        <SubTitle>{S.heading}</SubTitle>
        <MldPreviewLabel />
      </div>

      {query.isLoading && (
        <div
          className="flex items-center justify-center gap-3 py-6 text-sm text-neutral-600"
          role="status"
          data-testid="mld-member-loading"
        >
          <Spinner aria-hidden="true" />
          <span>{S.loading}</span>
        </div>
      )}

      {query.isError && (
        <Alert
          color="failure"
          icon={HiOutlineExclamation}
          data-testid="mld-member-error"
        >
          <div className="flex flex-wrap items-center gap-4">
            <span>{S.loadError}</span>
            <Button
              variant="LIGHT"
              size="xs"
              isProcessing={query.isFetching}
              disabled={query.isFetching}
              onClick={() => void query.refetch()}
            >
              {S.retry}
            </Button>
          </div>
        </Alert>
      )}

      {query.isSuccess &&
        (deals.length === 0 ? (
          <p
            className="text-sm text-neutral-600"
            data-testid="mld-member-empty"
          >
            {S.empty}
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-4" data-testid="mld-deal-cards">
              {deals.map((deal) => (
                <DealCard
                  key={deal.subscriptionId}
                  deal={deal}
                  customerId={customerId}
                  options={formatOptions}
                />
              ))}
            </div>
            {/* Keyed on the member, so another member starts on "All". */}
            <MemberLedger
              key={customerId}
              deals={deals}
              options={formatOptions}
            />
          </>
        ))}
    </div>
  );
};

export default MemberMldTab;
